import { useDispatch, useSelector } from "react-redux";
import { useState, useCallback, useRef } from "react";
import clsx from "clsx";
import useLanguageChange from "../../hooks/useLanguageChange";
import { setLanguageDrawerOpen } from "../../reducers/audioTranslation-reducer";
import { startAudioTranslation } from "../../actions/audioTranslationAction";
import { TRANSLATION_LANGUAGE_OPTIONS, VOICE_TYPES } from "../../constants/AudioTranslationConstants";
import { setVoiceType } from "../../reducers/audioTranslation-reducer";
import { GLOBAL_MEETING_DATA } from "../../constants/PublicVariables";
import { KEYBOARD_KEYS } from "../../constants/Constants";
import "./audioTranslation.scss";

const LanguageDropdown = ({ label, selectedKey, onSelect, excludeKey }) => {
    const toggleRef = useRef(null);
    const selectedLang = TRANSLATION_LANGUAGE_OPTIONS.find((l) => l.key === selectedKey);
    const filteredLanguages = TRANSLATION_LANGUAGE_OPTIONS.filter((lang) => lang.key !== excludeKey);

    const handleSelect = (key) => {
        onSelect(key);
        // Programmatically close the Bootstrap dropdown
        toggleRef.current?.click();
    };

    return (
        <div className='form-group'>
            <label className='form-label'>{label}</label>
            <div className='dropdown custom-dropdown dropdown-select call-settings-dropdown'>
                <button ref={toggleRef} className='btn dropdown-toggle triangle-none' type='button' data-bs-toggle='dropdown' aria-expanded='false'>
                    <span className='btn-text'>{selectedLang?.displayName || "Select"}</span>
                    <span className='dropdown-toggle-arrow icon-down-arrow-light'></span>
                </button>
                <div className='dropdown-menu'>
                    <div className='dropdown-menu-in custom-scroll-thin'>
                        <ul>
                            {filteredLanguages.map((lang) => (
                                <li key={lang.key}>
                                    <button
                                        className={clsx("btn dropdown-item", selectedKey === lang.key && "active")}
                                        onClick={() => handleSelect(lang.key)}
                                        type='button'
                                    >
                                        {lang.displayName}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AudioTranslationDrawer = () => {
    const dispatch = useDispatch();
    const localisedValues = useLanguageChange();
    const { isLanguageDrawerOpen, voiceType } = useSelector((state) => state.audioTranslation);

    const isAgent = GLOBAL_MEETING_DATA.isAgent;

    const [selectedSourceKey, setSelectedSourceKey] = useState(isAgent ? "" : "en_US");
    const [selectedTargetKey, setSelectedTargetKey] = useState(isAgent ? "en_US" : "");

    const handleStartTranslation = () => {
        const sourceConfig = TRANSLATION_LANGUAGE_OPTIONS.find((l) => l.key === selectedSourceKey);
        const targetConfig = TRANSLATION_LANGUAGE_OPTIONS.find((l) => l.key === selectedTargetKey);
        if (sourceConfig && targetConfig) {
            dispatch(startAudioTranslation(sourceConfig.speechCode, targetConfig.speechCode, targetConfig.translateCode));
        }
    };

    const handleClose = useCallback(() => {
        dispatch(setLanguageDrawerOpen(false));
    }, [dispatch]);

    const handleSwapLanguages = () => {
        const prevSource = selectedSourceKey;
        const prevTarget = selectedTargetKey;
        setSelectedSourceKey(prevTarget);
        setSelectedTargetKey(prevSource);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Escape" && e.keyCode === KEYBOARD_KEYS.ESCAPE) {
            handleClose();
        }
    };

    if (!isLanguageDrawerOpen) return null;

    return (
        <div className='audio-translation-modal-overlay' onClick={handleClose} onKeyDown={handleKeyDown}>
            <div className='audio-translation-modal' onClick={(e) => e.stopPropagation()}>
                <div className='modal-header'>
                    <h5 className='modal-title'>{localisedValues?.audio_translation_setup || "Audio Translation"}</h5>
                    <button type='button' className='btn-close' onClick={handleClose} aria-label='Close'>
                        <span className='btn-icon icon-close-light'></span>
                    </button>
                </div>
                <div className='modal-body'>
                    <div className='language-row'>
                        <LanguageDropdown
                            label={localisedValues?.they_speak || "They speak"}
                            selectedKey={selectedSourceKey}
                            onSelect={setSelectedSourceKey}
                            excludeKey={selectedTargetKey}
                        />
                        <div className='swap-wrapper'>
                            <button
                                className='btn btn-swap'
                                type='button'
                                onClick={handleSwapLanguages}
                                disabled={!selectedSourceKey || !selectedTargetKey}
                                title='Swap languages'
                            >
                                <span className='icon-swap-arrows'>&#8646;</span>
                            </button>
                        </div>
                        <LanguageDropdown
                            label={localisedValues?.i_want_to_hear || "I want to hear"}
                            selectedKey={selectedTargetKey}
                            onSelect={setSelectedTargetKey}
                            excludeKey={selectedSourceKey}
                        />
                    </div>
                    <div className='voice-type-row'>
                        <label className='form-label'>{localisedValues?.voice_type || "Voice"}</label>
                        <div className='voice-type-toggle'>
                            <button
                                className={clsx("btn btn-voice-type", voiceType === VOICE_TYPES.FEMALE && "active")}
                                type='button'
                                onClick={() => dispatch(setVoiceType(VOICE_TYPES.FEMALE))}
                            >
                                {localisedValues?.voice_female || "Female"}
                            </button>
                            <button
                                className={clsx("btn btn-voice-type", voiceType === VOICE_TYPES.MALE && "active")}
                                type='button'
                                onClick={() => dispatch(setVoiceType(VOICE_TYPES.MALE))}
                            >
                                {localisedValues?.voice_male || "Male"}
                            </button>
                        </div>
                    </div>
                </div>
                <div className='modal-footer'>
                    <div className='button-inline'>
                        <div className='button'>
                            <button className='btn btn-secondary' type='button' onClick={handleClose}>
                                {localisedValues?.cancel || "Cancel"}
                            </button>
                        </div>
                        <div className='button'>
                            <button
                                className='btn btn-primary'
                                type='button'
                                onClick={handleStartTranslation}
                                disabled={!selectedSourceKey || !selectedTargetKey}
                            >
                                {localisedValues?.start_translation || "Start Translation"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AudioTranslationDrawer;
